import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { toast } from 'react-toastify';

interface TournamentEntry {
  id: string;
  tournamentName: string;
  date: string;
  location: string;
  category: string;
  result: string;
  points: number;
  ranking: number;
}

interface TournamentRankingProps {
  player: any;
}

const TournamentRanking: React.FC<TournamentRankingProps> = ({ player }) => {
  const [loading, setLoading] = useState(false);
  const [tournaments, setTournaments] = useState<TournamentEntry[]>([]);
  const [totalTournaments, setTotalTournaments] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [averageRanking, setAverageRanking] = useState(0);

  useEffect(() => {
    if (player?.id) {
      loadTournamentData();
    }
  }, [player?.id]);

  const loadTournamentData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/ranking-evaluations/tournament/player/${player.id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTournaments(data.data || []);
          setTotalTournaments(data.data?.length || 0);
          const points = data.data?.reduce((sum: number, t: TournamentEntry) => sum + (t.points || 0), 0) || 0;
          setTotalPoints(points);
          const rankings = data.data?.filter((t: TournamentEntry) => t.ranking > 0) || [];
          const avgRanking = rankings.length > 0
            ? rankings.reduce((sum: number, t: TournamentEntry) => sum + t.ranking, 0) / rankings.length
            : 0;
          setAverageRanking(avgRanking);
        }
      }
    } catch (error) {
      console.error('Error loading tournament data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!player || !player.id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#9c27b0' }}>
          Tournament Ranking
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Tournament ranking is based on the number of tournaments you have played and your performance.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        🏆 Your tournament ranking is calculated based on the number of tournaments played, results, and points earned.
      </Alert>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#9c27b0', color: 'white' }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total Tournaments
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {totalTournaments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1976d2', color: 'white' }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total Points
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {totalPoints}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#2e7d32', color: 'white' }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Average Ranking
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {averageRanking > 0 ? averageRanking.toFixed(1) : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#ed6c02', color: 'white' }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Ranking Score
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {totalTournaments > 0 ? Math.round((totalPoints / totalTournaments) * 10) : 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tournament List */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Tournament History
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : tournaments.length > 0 ? (
        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tournament Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Result</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Ranking</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Points</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tournaments.map((tournament) => (
                <TableRow key={tournament.id} hover>
                  <TableCell>
                    {new Date(tournament.date).toLocaleDateString()}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{tournament.tournamentName}</TableCell>
                  <TableCell>{tournament.location}</TableCell>
                  <TableCell>
                    <Chip label={tournament.category} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tournament.result}
                      size="small"
                      color={
                        tournament.result.toLowerCase().includes('win') || tournament.result.toLowerCase().includes('champion')
                          ? 'success'
                          : tournament.result.toLowerCase().includes('final')
                          ? 'primary'
                          : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {tournament.ranking > 0 ? (
                      <Chip
                        label={`#${tournament.ranking}`}
                        size="small"
                        color={tournament.ranking <= 3 ? 'success' : tournament.ranking <= 8 ? 'warning' : 'default'}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{tournament.points}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">
          No tournament records found. Tournament data will be added by coaches/admins.
        </Alert>
      )}
    </Box>
  );
};

export default TournamentRanking;

